/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useCallback, useMemo } from 'react';
import t from 'react-translate';

// Redux
import { useSelector } from 'react-redux';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';

// Styles
import { css } from '@emotion/react';
import {
  generalQuestionBackgroundColor,
  menteeLightBackgroundColor,
  mentorLightBackgroundColor,
} from 'athena/styles/colors';
import { headerLineHeight } from 'styles/global_defaults/fonts';
import { quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';

// Components
import NvTooltip from 'shared/components/nv-tooltip';
import ParticipantDot from 'athena/components/participant-dot';
import MentoringProgramContext from 'athena/components/mentoring-program/context';


const backgroundColor = {
  [MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE]: menteeLightBackgroundColor,
  [MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR]: mentorLightBackgroundColor,
  [MentoringProgramRole.MENTORSHIP_PROGRAM_ADMIN]: generalQuestionBackgroundColor,
};

type QuestionWithBackgroundProps = {
  question: string;
  role: MentoringProgramRole;
};

const ParticipantQuestionDisplay = ({ question, role }: QuestionWithBackgroundProps) => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const mentoringProgramAliases = useSelector((state) => (
    getMentoringProgramAliases(state, mentoringProgram?.id)
  ));

  const styles = useMemo(() => css`
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    padding-left: ${quarterSpacing}px;
    border-radius: ${quarterSpacing}px;
    margin-bottom: ${standardSpacing}px;
    line-height: ${headerLineHeight}px !important;
    background-color: ${backgroundColor[role]};
  `, [role]);

const getTooltipText = useCallback(() =>
    role === MentoringProgramRole.MENTORSHIP_PROGRAM_ADMIN
      ? ''
      : t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS.MATCHING_TYPE.CONFIGURATION.ASSIGNED.MATCHING_RULES.LIST.PARTICIPANT_QUESTION.TOOLTIP({
        role,
        ...mentoringProgramAliases,
      }
    ), [
      role,
      mentoringProgramAliases.MenteeAlias,
      mentoringProgramAliases.MentorAlias,
    ]
  );

  return (
    <NvTooltip
      text={getTooltipText()}
      enabled={role !== MentoringProgramRole.MENTORSHIP_PROGRAM_ADMIN}
      placement={role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE ? 'top' : 'bottom'}
    >
      <span css={styles} className='text-large-body semi-bold px-2 py-1 mr-1 mb-1'>
        {role !== MentoringProgramRole.MENTORSHIP_PROGRAM_ADMIN && (
          <ParticipantDot role={role} className='mr-1' />
        )}
        {question}
      </span>
    </NvTooltip>
  );
};

export default ParticipantQuestionDisplay;
